import { MapPin } from "lucide-react";
import { Reveal, PageHeader, SectionLabel } from "@/components/Reveal";
import { IMAGES, CERTIFICATIONS, LOCATIONS, STATS } from "@/data/content";

const MissionVision = () => (
  <section className="py-24 px-6 sm:px-12" data-testid="mission-vision-section">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-7">
        <Reveal>
          <SectionLabel>Who We Are</SectionLabel>
          <p className="mt-6 text-lg leading-relaxed text-slate-600">
            Auric Valuation Pvt. Ltd. is a leading valuation advisory firm providing credible,
            independent and high-quality valuation services for a wide spectrum of assets. Backed by
            a team of qualified professionals and domain experts, we deliver accurate and unbiased
            valuations that empower businesses and financial institutions to make informed decisions.
          </p>
          <p className="mt-6 text-base leading-relaxed text-slate-600">
            The company is led by Er. Manish Kumar Sharma and operates with practical understanding
            of local property markets, lender requirements, site observations and report
            documentation needs — with coverage across major cities and districts of Rajasthan and beyond.
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-5 grid gap-px bg-border border border-border self-start">
        <Reveal className="bg-white">
          <div className="p-10">
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-gold">Our Mission</p>
            <p className="mt-4 font-serif text-xl leading-relaxed text-ink">
              To deliver reliable valuation solutions with integrity, transparency and
              professionalism, adding value to our clients' decision-making process.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="bg-white">
          <div className="p-10">
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-gold">Our Vision</p>
            <p className="mt-4 font-serif text-xl leading-relaxed text-ink">
              To be recognized as the most trusted valuation partner, known for our expertise,
              ethics and commitment to excellence.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Certifications = () => (
  <section className="bg-ink text-cream py-24 px-6 sm:px-12" data-testid="certifications-section">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <SectionLabel light>Certifications & Affiliations</SectionLabel>
        <h2 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight max-w-2xl">
          Adherence to internationally recognized standards.
        </h2>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {CERTIFICATIONS.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.1} className="bg-ink">
            <div className="p-8 hover:bg-white/5 transition-colors duration-500 h-full" data-testid={`cert-card-${i}`}>
              <span className="bg-white px-5 py-4 flex items-center justify-center w-full h-28">
                <img src={c.logo} alt={`${c.title} official logo`} data-testid={`cert-logo-${i}`} className="max-h-full max-w-full w-auto h-auto object-contain" />
              </span>
              <p className="mt-6 font-serif text-2xl font-bold text-white">{c.title}</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">{c.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <p className="mt-10 text-sm text-slate-400 max-w-2xl">
          We adhere to internationally recognized standards and best practices to ensure accuracy,
          reliability and professional excellence in every valuation we deliver.
        </p>
      </Reveal>
    </div>
  </section>
);

const Strengths = () => (
  <section className="py-24 px-6 sm:px-12 bg-white border-b border-border" data-testid="strengths-section">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <SectionLabel>Our Strengths</SectionLabel>
      </Reveal>
      <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.06} className="bg-white">
            <div className="p-8 sm:p-10 hover:-translate-y-1 transition-transform duration-300" data-testid={`strength-card-${i}`}>
              <p className="font-serif text-3xl sm:text-4xl font-bold text-gold">{s.value}</p>
              <p className="mt-3 text-xs leading-relaxed text-slate-500">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const TeamBand = () => (
  <section className="py-24 px-6 sm:px-12" data-testid="team-section">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6">
        <Reveal>
          <div className="clipped-frame">
            <img src={IMAGES.team} alt="Auric Valuation professional team" className="w-full h-[400px] object-cover" />
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-6">
        <Reveal>
          <SectionLabel>Our Professional Team</SectionLabel>
          <h2 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight text-ink">
            50+ qualified professionals across domains.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-slate-600">
            Our team consists of qualified valuers, engineers, finance professionals and analysts
            with rich experience across domains. We continuously invest in skill development,
            knowledge upgradation and technology to deliver best-in-class services to our clients.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const Locations = () => (
  <section className="py-24 px-6 sm:px-12 bg-white border-t border-border" data-testid="locations-section">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <SectionLabel>Service Locations</SectionLabel>
        <h2 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight text-ink max-w-2xl">
          Serving major cities and districts across Rajasthan and beyond.
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-5">
        {LOCATIONS.map((l, i) => (
          <Reveal key={l} delay={(i % 8) * 0.04} y={12}>
            <p className="flex items-center justify-center gap-2 text-center font-serif text-base font-semibold text-ink border-b border-border pb-3 hover:text-gold hover:border-gold transition-colors duration-300" data-testid={`location-${i}`}>
              <MapPin size={16} className="text-gold shrink-0" />
              {l}
            </p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function About() {
  return (
    <>
      <PageHeader
        label="About Auric Valuation"
        lines={["Credibility, independence,", "and craft."]}
        sub="A Jaipur-based valuation advisory firm serving financial institutions, corporates and property stakeholders across Rajasthan and beyond."
      />
      <MissionVision />
      <Certifications />
      <Strengths />
      <TeamBand />
      <Locations />
    </>
  );
}
